/**
 * Custom hook for managing content filters form state
 *
 * Extracted to reduce component complexity and improve maintainability.
 */

import { ContentType, FEATURE_FLAGS } from '@core/domain';
import { ContentType as SchemaContentType } from '@core/graphql/types/schema';
import { useIsFeatureEnabled } from '@core/hooks';
import type { SampleFilters } from '@internal-audit/domain';
import type { AuditSessionFilterOptions } from '@internal-audit/hooks/useAuditSessionFilterOptions';
import { useCallback, useMemo } from 'react';

interface UseContentFiltersFormProps {
  filters: SampleFilters;
  filterOptions: AuditSessionFilterOptions | null;
  onFiltersChange: (filters: SampleFilters) => void;
}

// Helper functions to create filter options
const createLabelOptions = (labels: Array<{ id: string; labelName: string }>) =>
  labels.map(label => ({
    id: label.id,
    label: label.labelName,
    value: label.labelName,
  }));

const createSeverityOptions = (levels: number[]) =>
  levels.map(level => ({
    id: `level-${level}`,
    label: `Level ${level}`,
    value: level.toString(),
  }));

const createTopicOptions = (topics: string[]) =>
  topics.map(topic => ({
    id: topic,
    label: topic.charAt(0).toUpperCase() + topic.slice(1),
    value: topic,
  }));

const createLanguageOptions = (languages: string[]) =>
  languages.map(language => ({
    id: language,
    label: language.toUpperCase(),
    value: language,
  }));

const createContentTypeOptions = (contentTypes: string[]) =>
  contentTypes.map(contentType => ({
    id: contentType,
    label: contentType.charAt(0).toUpperCase() + contentType.slice(1),
    value: contentType,
  }));

export function useContentFiltersForm({ filters, filterOptions, onFiltersChange }: UseContentFiltersFormProps) {
  const isUsernameClassificationEnabled = useIsFeatureEnabled(FEATURE_FLAGS.USERNAME_CLASSIFICATION);

  const handleLabelsChange = useCallback((values: string[]) => onFiltersChange({ ...filters, labels: values }), [filters, onFiltersChange]);

  const handleSeverityChange = useCallback(
    (values: string[]) => {
      const severityNumbers = values.map(v => parseInt(v, 10));

      onFiltersChange({ ...filters, labelSeverities: severityNumbers });
    },
    [filters, onFiltersChange]
  );

  const handleTopicsChange = useCallback((values: string[]) => onFiltersChange({ ...filters, topics: values }), [filters, onFiltersChange]);

  const handleLanguagesChange = useCallback((values: string[]) => onFiltersChange({ ...filters, languages: values }), [filters, onFiltersChange]);

  const handleContentTypesChange = useCallback((values: string[]) => onFiltersChange({ ...filters, contentTypes: values }), [filters, onFiltersChange]);

  const handleCustomValuesChange = useCallback(
    (customValues: Record<string, string> | undefined) => onFiltersChange({ ...filters, customValues }),
    [filters, onFiltersChange]
  );

  const handleViolatesPolicyChange = useCallback(
    (value: boolean | undefined) => onFiltersChange({ ...filters, violatesPolicy: value }),
    [filters, onFiltersChange]
  );

  // Memoized filter options to reduce complexity
  const filterOptionsMemo = useMemo(
    // eslint-disable-next-line complexity
    () => ({
      labelOptions: filterOptions?.standardLabels
        ? createLabelOptions(
            isUsernameClassificationEnabled
              ? filterOptions.standardLabels
              : filterOptions.standardLabels.filter(label => label.contentType !== SchemaContentType.Username)
          )
        : [],
      severityOptions: filterOptions?.severityLevels ? createSeverityOptions(filterOptions.severityLevels) : [],
      topicOptions: filterOptions?.topics ? createTopicOptions(filterOptions.topics) : [],
      languageOptions: filterOptions?.languages ? createLanguageOptions(filterOptions.languages) : [],
      contentTypeOptions: filterOptions?.contentTypes
        ? createContentTypeOptions(
            isUsernameClassificationEnabled ? filterOptions.contentTypes : filterOptions.contentTypes.filter(ct => ct !== ContentType.USERNAME)
          )
        : [],
    }),
    [filterOptions, isUsernameClassificationEnabled]
  );

  return {
    handleLabelsChange,
    handleSeverityChange,
    handleTopicsChange,
    handleLanguagesChange,
    handleContentTypesChange,
    handleCustomValuesChange,
    handleViolatesPolicyChange,
    filterOptions: filterOptionsMemo,
  };
}
